import { useEffect, useState } from 'react';
import EChart from '../../components/Charts/EChart';
import { analyticsApi } from '../../services/api';
import toast from 'react-hot-toast';

export default function SubscriptionsTab({ dateRange }) {
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [churnData, setChurnData] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const getDaysFromRange = () => {
    return dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
  };

  async function loadData() {
    try {
      setLoading(true);
      const days = getDaysFromRange();

      const [subscriptions, revenue, timeline, churn] = await Promise.all([
        analyticsApi.subscriptions(),
        analyticsApi.revenue(),
        analyticsApi.subscriptionTimeline(days),
        analyticsApi.churn(days)
      ]);

      setSubscriptionData(subscriptions);
      setRevenueData(revenue);
      setTimelineData(timeline);
      setChurnData(churn);
    } catch (error) {
      console.error('Failed to load subscription analytics:', error);
      toast.error('Failed to load subscription analytics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  const statusChartData = transformStatusData(subscriptionData);
  const timelineChartData = transformTimelineData(timelineData);

  return (
    <div className="space-y-6">
      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="MRR"
          value={`$${(revenueData?.mrr || 0).toFixed(2)}`}
          subtitle="Monthly Recurring Revenue"
          icon="💰"
          color="green"
        />
        <MetricCard
          title="ARR"
          value={`$${(revenueData?.arr || 0).toFixed(2)}`}
          subtitle="Annual Recurring Revenue"
          icon="📈"
          color="blue"
        />
        <MetricCard
          title="ARPU"
          value={`$${(revenueData?.arpu || 0).toFixed(2)}`}
          subtitle="Average Revenue Per User"
          icon="👤"
          color="purple"
        />
        <MetricCard
          title="Paying Users"
          value={revenueData?.paying_users || 0}
          subtitle="Active premium subscribers"
          icon="⭐"
          color="yellow"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Subscription Timeline */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription Timeline</h2>
          <EChart option={timelineChartData} height="300px" />
        </div>

        {/* Status Breakdown */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription Status</h2>
          <EChart option={statusChartData} height="300px" />
        </div>
      </div>

      {/* Churn Metrics */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Churn Analysis ({dateRange})</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ChurnMetric
            label="Churn Rate"
            value={`${(churnData?.churn_rate || 0).toFixed(2)}%`}
            description="Percentage of subscribers who canceled"
            color="red"
          />
          <ChurnMetric
            label="Growth Rate"
            value={`${(churnData?.growth_rate || 0).toFixed(2)}%`}
            description="Net subscription growth rate"
            color="green"
          />
          <ChurnMetric
            label="New Subscriptions"
            value={churnData?.new_subscriptions || 0}
            description="New subscribers in period"
            color="blue"
          />
          <ChurnMetric
            label="Cancellations"
            value={churnData?.canceled_subscriptions || 0}
            description="Canceled subscriptions in period"
            color="orange"
          />
        </div>
      </div>

      {/* Subscription Details */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription Details</h2>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatItem label="Active" value={subscriptionData?.subscriptions?.active || 0} />
          <StatItem label="Canceled" value={subscriptionData?.subscriptions?.canceled || 0} />
          <StatItem label="Past Due" value={subscriptionData?.subscriptions?.past_due || 0} />
          <StatItem label="Trialing" value={subscriptionData?.subscriptions?.trialing || 0} />
          <StatItem label="Premium Users" value={subscriptionData?.users?.premium || 0} />
          <StatItem label="Free Users" value={subscriptionData?.users?.free || 0} />
        </dl>
      </div>
    </div>
  );
}

function transformStatusData(data) {
  if (!data) {
    return {
      series: []
    };
  }

  const chartData = [
    { value: data.subscriptions?.active || 0, name: 'Active' },
    { value: data.subscriptions?.canceled || 0, name: 'Canceled' },
    { value: data.subscriptions?.past_due || 0, name: 'Past Due' },
    { value: data.subscriptions?.trialing || 0, name: 'Trialing' }
  ];

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'horizontal',
      bottom: 0,
      data: ['Active', 'Canceled', 'Past Due', 'Trialing']
    },
    series: [
      {
        name: 'Subscriptions',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{d}%'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: true
        },
        data: chartData,
        color: ['#10b981', '#ef4444', '#f59e0b', '#3b82f6']
      }
    ]
  };
}

function transformTimelineData(data) {
  if (!data?.timeline || data.timeline.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Subscriptions' },
      series: []
    };
  }

  const categories = data.timeline.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const newSubs = data.timeline.map(item => item.new_subscriptions || 0);
  const cancellations = data.timeline.map(item => Math.abs(item.cancellations || 0));
  const netChange = data.timeline.map(item => item.net_change || 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    legend: {
      data: ['New Subscriptions', 'Cancellations', 'Net Change'],
      top: 0
    },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Subscriptions',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'New Subscriptions',
        type: 'line',
        data: newSubs,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.4)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
            ]
          }
        },
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 2 }
      },
      {
        name: 'Cancellations',
        type: 'line',
        data: cancellations,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.4)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
            ]
          }
        },
        itemStyle: { color: '#ef4444' },
        lineStyle: { width: 2 }
      },
      {
        name: 'Net Change',
        type: 'line',
        data: netChange,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.4)' },
              { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
            ]
          }
        },
        itemStyle: { color: '#3b82f6' },
        lineStyle: { width: 2 }
      }
    ]
  };
}

function MetricCard({ title, value, subtitle, icon, color }) {
  const colorClasses = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600'
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow card-hover">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className={`text-2xl p-2 rounded-lg ${colorClasses[color]}`}>{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function ChurnMetric({ label, value, description, color }) {
  const colorClasses = {
    red: 'text-red-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600'
  };

  return (
    <div className="text-center">
      <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-600">{label}</dt>
      <dd className="mt-1 text-2xl font-bold text-gray-900">{value.toLocaleString()}</dd>
    </div>
  );
}
